import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../../types";
import { IDENTIFY_CUSTOMER_RESERVED_SOCKET_IDS } from "../../steps/ai/identify-customer.step";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

/**
 * Socket type for a single AR_CUSTOMER entity ref. Used by `identify_customer`
 * per-route and `others` sockets.
 */
const AR_CUSTOMER_REF: SocketType =
  `entity_reference:${SystemEntity.AR_CUSTOMER}` as SocketType;

/**
 * Generate output sockets for the identify_customer step.
 *
 * Always emits a `top_pick` socket (json match object, or SOCKET_NOT_TAKEN when
 * no pick meets the threshold).
 * Adds one entity-ref socket per configured customer route (id =
 * route.id, name = route.label or prettified id). Optionally adds an
 * `others` socket when `enableOthersRoute` is set.
 *
 * With no routes configured and no Others toggle, returns just the
 * `top_pick` socket so the user always sees the canonical output.
 */
export function generateIdentifyCustomerOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const routes = configEntryList<{ id?: unknown; label?: unknown }>(
    config.customerRoutes,
  );
  const enableOthers = config.enableOthersRoute === true;
  const othersNullOnNoMatch = config.othersEmitNullWhenNoMatch === true;

  const sockets: DynamicSocketDefinition[] = [
    {
      id: "top_pick",
      name: "Top Pick",
      // { customerId, confidence, ... } or SOCKET_NOT_TAKEN when below threshold
      type: "json" as SocketType,
      required: false,
      description:
        "Top customer match with confidence (not-taken sentinel if no match meets threshold)",
    },
  ];

  const { sockets: routeSockets } = buildDynamicSockets({
    reserved: IDENTIFY_CUSTOMER_RESERVED_SOCKET_IDS,
    version: effectiveSocketIdVersion("identify_customer", config),
    entries: routes
      // The id shape is a config rule, not a collision rule, so it applies at
      // every version.
      .filter(
        (route): route is { id: string; label?: unknown } =>
          typeof route.id === "string" && /^[a-z][a-z0-9_]*$/.test(route.id),
      )
      .map((route) => ({
        name: route.id,
        socket: {
          name:
            typeof route.label === "string" && route.label.length > 0
              ? route.label
              : prettify(route.id),
          type: AR_CUSTOMER_REF,
          required: false,
          description: `Customer ref when matched customer routes to "${route.id}"`,
        },
      })),
  });
  sockets.push(...routeSockets);

  if (enableOthers) {
    sockets.push({
      id: "others",
      name: "Others",
      type: AR_CUSTOMER_REF,
      required: false,
      description: othersNullOnNoMatch
        ? "Customer ref when matched but not on a named route; null when no customer matched (downstream still runs)"
        : "Customer ref when matched but does not match any configured route",
    });
  }

  return sockets;
}
